// 組織内で登録・利用するConnect関連のAPI

import {
  ConnectBody,
  ConnectDataTypeVersionOptionsRaw,
  ConnectNotificationChannel,
  ConnectRaw,
} from "@/domain/Connect";
import { API_CLIENT } from "../config";
import { downloadFileFromAPI, getAPIConfigs } from "../helpers";

const apiUrl = "/connects";
const apiConnectExecutionUrl = "/connect_job";

const getNotificationWebhookUrlPayload = ({
  connect_notification_channel,
  clearWithEmptyString,
}: {
  connect_notification_channel?: ConnectNotificationChannel | null;
  clearWithEmptyString: boolean;
}) => {
  if (connect_notification_channel === undefined) {
    return undefined;
  }

  if (connect_notification_channel?.slack_webhook?.webhook_url) {
    return connect_notification_channel.slack_webhook.webhook_url;
  }

  return clearWithEmptyString ? "" : null;
};

export const getConnects = async ({
  organizationId,
}: {
  organizationId: string;
}): Promise<ConnectRaw[]> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.get<{ items: ConnectRaw[] }>(apiUrl, {
    ...apiOrganizationIdParams,
  });
  return response.data.items;
};

export const getConnect = async ({
  organizationId,
  connect_id,
}: {
  organizationId: string;
  connect_id: string;
}): Promise<ConnectRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.get<{ item: ConnectRaw }>(
    `${apiUrl}/${connect_id}`,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const postCreateConnect = async ({
  organizationId,
  connect,
  connect_notification_channel,
}: {
  organizationId: string;
  connect: ConnectBody & { connect_secrets: { [k: string]: string } };
  connect_notification_channel?: ConnectNotificationChannel | null;
}): Promise<ConnectRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const notification_webhook_url = getNotificationWebhookUrlPayload({
    connect_notification_channel,
    clearWithEmptyString: false,
  });
  const payload =
    notification_webhook_url !== undefined
      ? { ...connect, notification_webhook_url }
      : connect;
  const response = await API_CLIENT.post<{ item: ConnectRaw }>(
    apiUrl,
    payload,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const postEditConnect = async ({
  organizationId,
  connect_id,
  connect,
  connect_notification_channel,
}: {
  organizationId: string;
  connect_id: string;
  connect: ConnectBody;
  connect_notification_channel?: ConnectNotificationChannel | null;
}): Promise<ConnectRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const notification_webhook_url = getNotificationWebhookUrlPayload({
    connect_notification_channel,
    clearWithEmptyString: true,
  });
  const payload =
    notification_webhook_url !== undefined
      ? { ...connect, notification_webhook_url }
      : connect;
  const response = await API_CLIENT.post<{ item: ConnectRaw }>(
    `${apiUrl}/${connect_id}`,
    payload,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const getConnectNotificationChannel = async ({
  organizationId,
  connect_id,
}: {
  organizationId: string;
  connect_id: string;
}): Promise<ConnectNotificationChannel> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.get<{ item: ConnectNotificationChannel }>(
    `${apiUrl}/${connect_id}/notification_channel`,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const getConnectSecrets = async ({
  organizationId,
  connect_id,
}: {
  organizationId: string;
  connect_id: string;
}): Promise<{ [k: string]: string }> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.get<{ item: { [k: string]: string } }>(
    `${apiUrl}/${connect_id}/connect_secrets`,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const postEditConnectSecrets = async ({
  organizationId,
  connect_id,
  item,
}: {
  organizationId: string;
  connect_id: string;
  item: { [k: string]: string };
}): Promise<{ secret: string }> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.post<{ secret: string }>(
    `${apiUrl}/${connect_id}/connect_secrets`,
    { item },
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data;
};

export const postDeleteConnect = async ({
  organizationId,
  connect_id,
}: {
  organizationId: string;
  connect_id: string;
}): Promise<ConnectRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.post<{ item: ConnectRaw }>(
    `${apiUrl}/${connect_id}/delete`,
    undefined,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const postEnableConnect = async ({
  organizationId,
  connect_id,
}: {
  organizationId: string;
  connect_id: string;
}): Promise<ConnectRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.post<{ item: ConnectRaw }>(
    `${apiUrl}/${connect_id}/enable`,
    undefined,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const postRemoveConnect = async ({
  organizationId,
  connect_id,
}: {
  organizationId: string;
  connect_id: string;
}): Promise<ConnectRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.post<{ item: ConnectRaw }>(
    `${apiUrl}/${connect_id}/remove`,
    undefined,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

// Connectを手動で起動する
export const postExecuteConnect = async ({
  organizationId,
  connect_id,
  runtime_parameters,
  send_data_type_version_options,
  receive_data_type_version_options,
  output_version_title,
  output_version_category_id,
}: {
  organizationId: string;
  connect_id: string;
  runtime_parameters?: { [key: string]: string };
  send_data_type_version_options?: ConnectDataTypeVersionOptionsRaw;
  receive_data_type_version_options?: ConnectDataTypeVersionOptionsRaw;
  output_version_title?: string;
  output_version_category_id?: string;
}): Promise<{ result: string }> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const apiParams = {
    params: { ...apiOrganizationIdParams?.params, connect_id: connect_id },
  };
  const payload: any = {};
  if (runtime_parameters) {
    payload.runtime_parameters = runtime_parameters;
  }
  if (send_data_type_version_options) {
    payload.send_data_type_version_options = send_data_type_version_options;
  }
  if (receive_data_type_version_options) {
    payload.receive_data_type_version_options =
      receive_data_type_version_options;
  }
  // 前後の空白を落とし, 空になるなら送らない (サーバー側は空白のみも 400 とする)
  // カテゴリーと違い, バージョン名の空文字列には意味が無いため undefined 判定にしない
  const trimmedTitle = output_version_title?.trim();
  if (trimmedTitle) {
    payload.output_version_title = trimmedTitle;
  }
  // 空文字列は「未分類」を意味するため, undefined のときだけ送らない
  // （未指定 = 出力先バージョンのカテゴリーを継承）
  if (output_version_category_id !== undefined) {
    payload.output_version_category_id = output_version_category_id;
  }
  const response = await API_CLIENT.post<{ result: string }>(
    apiConnectExecutionUrl,
    Object.keys(payload).length > 0 ? payload : undefined,
    {
      ...apiParams,
    }
  );
  return response.data;
};

export const postCancelConnectJob = async ({
  organizationId,
  connect_id,
  connect_job_history_id,
}: {
  organizationId: string;
  connect_id: string;
  connect_job_history_id: string;
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const apiParams = {
    params: {
      ...apiOrganizationIdParams?.params,
      connect_id,
      connect_job_history_id,
    },
  };
  await API_CLIENT.post(
    `${apiConnectExecutionUrl}/cancel`,
    undefined,
    apiParams
  );
};
